import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { apiService } from "@/lib/api-service";
import type { Employee } from "@/types/employee.types";
import { toast } from "sonner";
import { useState } from "react";

interface EmployeeDeleteDialogProps {
  employee: Employee | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EmployeeDeleteDialog({
  employee,
  open,
  onOpenChange,
}: EmployeeDeleteDialogProps) {
  const queryClient = useQueryClient();

  const [isActionPending, setIsActionPending] = useState(false);

  const handleDelete = () => {
    if (!employee || !employee.id) return;

    setIsActionPending(true);

    toast.promise(apiService.delete(`/employees/${employee.id}`), {
      loading: "Deleting employee...",
      success: () => {
        queryClient.invalidateQueries({ queryKey: ["employees"] });
        onOpenChange(false);
        return "Employee deleted successfully";
      },
      error: (error: Error) => {
        return error.message || "Failed to delete employee";
      },
      finally: () => {
        setIsActionPending(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Delete Employee</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete {employee?.name}? This action cannot
            be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isActionPending}
          >
            {isActionPending ? "Deleting..." : "Delete Employee"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
